import AxiosInstance from 'config/apiServiceApiGateway'

export type OrderStatusEnum = 'PENDING' | 'COMPLETED' | 'EXPIRED' | 'FAILED' | 'CANCELED'

export type TaxInvoiceStatusFilterEnum = 'pending' | 'uploaded'

export type OrderDirectionEnum = 'ORDER_DIRECTION_UNSPECIFIED' | 'ASCENDING' | 'DESCENDING'

export type SellerEmailType = {
  role: string
  email: string
}

export type TaxInvoiceCustomBillingType = {
  id?: number
  npwp?: string
  name?: string
  address?: string
  bank_name?: string
  bank_account_number?: string
  bank_account_name?: string
}

export type TaxInvoiceSubmissionType = {
  submission_id: string
  invoice_id: string
  seller_id: string
  seller_name: string
  seller_emails: SellerEmailType[]
  top_up_account?: SellerEmailType
  pic_vendor_contacts?: SellerEmailType[]
  custom_billing?: TaxInvoiceCustomBillingType | null
  npwp: string
  amount: string
  topup_date?: string
  status: string
  tax_invoice_status: string
  tax_invoice_path: string
  tax_invoice_last_upload_date?: string
  tax_invoice_last_upload_by: string
}

export type TaxInvoiceFiltersType = {
  seller_name?: string
  seller_ids?: string[]
  statuses?: string[]
  tax_invoice_statuses?: TaxInvoiceStatusFilterEnum[]
  start_time?: string
  end_time?: string
}

export type TaxInvoiceListParamsType = {
  filters?: TaxInvoiceFiltersType
  page_size?: number
  page_cursor?: string
  sort_by?: string
  order_direction?: OrderDirectionEnum
}

export type TaxInvoicePaginationType = {
  next_cursor: string
  prev_cursor: string
}

export type TaxInvoiceListResponseType = {
  data: {
    tax_invoice_submissions: TaxInvoiceSubmissionType[]
  }
  pagination: TaxInvoicePaginationType
}

export type TaxInvoiceExportResponseType = {
  file_url: string
}

export type TaxInvoiceUploadRequestType = {
  submission_id: string
  seller_id: string
  file_url: string
}

export type TaxInvoiceUploadResponseType = {
  data: {
    message: string
  }
}

const LIST_PARAMS_CONFIG = { paramsSerializer: { indexes: null } }

/** Flatten nested filters into gRPC-gateway dotted query keys. */
export const flattenTaxInvoiceParams = (
  params: TaxInvoiceListParamsType,
): Record<string, string | number | string[] | undefined> => {
  const { filters, ...rest } = params
  const flat: Record<string, string | number | string[] | undefined> = { ...rest }

  if (filters?.seller_name) {
    flat['filters.seller_name'] = filters.seller_name
  }
  if (filters?.seller_ids?.length) {
    flat['filters.seller_ids'] = filters.seller_ids
  }
  if (filters?.statuses?.length) {
    flat['filters.statuses'] = filters.statuses
  }
  if (filters?.tax_invoice_statuses?.length) {
    flat['filters.tax_invoice_statuses'] = filters.tax_invoice_statuses
  }
  if (filters?.start_time) {
    flat['filters.start_time'] = filters.start_time
  }
  if (filters?.end_time) {
    flat['filters.end_time'] = filters.end_time
  }

  return flat
}

export const getTaxInvoiceSubmissions = (params: TaxInvoiceListParamsType) =>
  AxiosInstance.Get<TaxInvoiceListResponseType, true>({
    url: '/ads/internal/v1/creditmanager/tax-invoice/submissions',
    params: flattenTaxInvoiceParams({
      sort_by: 'client_timestamp',
      order_direction: 'DESCENDING',
      ...params,
    }),
    config: LIST_PARAMS_CONFIG,
  })

export const exportTaxInvoiceSubmissions = (
  params: Omit<
    TaxInvoiceListParamsType,
    'page_cursor' | 'page_size' | 'sort_by' | 'order_direction'
  >,
) =>
  AxiosInstance.Get<TaxInvoiceExportResponseType, true>({
    url: '/ads/internal/v1/creditmanager/tax-invoice/submissions/export',
    params: flattenTaxInvoiceParams(params),
    config: LIST_PARAMS_CONFIG,
  })

export const uploadTaxInvoice = (body: TaxInvoiceUploadRequestType) =>
  AxiosInstance.Post<TaxInvoiceUploadResponseType, true>({
    url: '/ads/internal/v1/creditmanager/tax-invoice/submissions/upload',
    data: body,
  })
